// src/lib/format-count.ts
//
// IR-1046 ⑤: compact number formatting, ported verbatim from iris-frontend's
// canonical helper (`src/utils/numberFormatter.ts`) so the OPS Analytics tab
// renders large counts "as done in IRIS" — 894K / 1.1M / 235.6K with the exact
// value available on hover. iris-ops and iris-frontend are separate repos, so
// the helper is copied rather than imported; keep the two in sync if the IRIS
// spec changes.

/**
 * Format a number into compact display (K/M suffixes).
 * Spec: values < 1000 show as-is, >= 1000 use K, >= 1M use M.
 * Round to 1 decimal, remove trailing .0, handle negatives, cap at 999.9M+.
 */
export function formatCount(value?: number | string | null): string {
  if (value === null || value === undefined) return "0";
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) return "0";

  const isNegative = num < 0;
  const absNum = Math.abs(num);

  let result: string;

  if (absNum < 1000) {
    result = absNum.toString();
  } else if (absNum >= 999_950_000) {
    result = "999.9M+";
  } else if (absNum >= 999_950) {
    // M range (includes K-to-M rollover: 999950/1000 = 999.95 rounds to 1000.0K)
    result = `${(absNum / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  } else {
    // K range
    result = `${(absNum / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return isNegative ? `-${result}` : result;
}

/**
 * Returns both the compact and exact representations + whether abbreviation
 * happened (so a caller can decide whether a hover-for-exact tooltip is useful).
 */
export function formatCountParts(value?: number | string | null): {
  formatted: string;
  exact: string;
  isAbbreviated: boolean;
} {
  const formatted = formatCount(value);

  if (value === null || value === undefined) {
    return { formatted: "0", exact: "0", isAbbreviated: false };
  }

  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) {
    return { formatted: "0", exact: "0", isAbbreviated: false };
  }

  const exact = num.toLocaleString();
  const isAbbreviated = Math.abs(num) >= 1000;

  return { formatted, exact, isAbbreviated };
}
